import router from '../../../router'
import { SortedTable, SortLink } from 'vue-sorted-table'
import NavigationBar from '../../../components/navigation-bar/NavigationBar'
import TabBar from '../../../components/tab-bar/TabBar'
import InputText from '../../../components/input-text/InputText'
import InputCheckbox from '../../../components/input-checkbox/InputCheckbox'
import InputDropdownFilter from '../../../components/input-dropdown-filter/InputDropdownFilter'
import InputDropdown from '../../../components/input-dropdown/InputDropdown'
import InputButton from '../../../components/input-button/InputButton'
import Loader from '../../../components/loader/Loader'
import SettingsHeader from '../../../components/settings-header/SettingsHeader'
import { AccountService, Account, CoupledUser } from '../../../services/account.service'
import { CustomerService, CustomerDropdown } from '../../../services/customer.service'
import { LocationService, LocationDropdown } from '../../../services/location.service'
import { Constants } from '../../../constants'
import { ScreenLoader } from '../../../utils/loading.util'
import {User} from "@/services/user.service";
import {KeycloakService} from "@/services/keycloak.service";

export default {
    name: 'Account',
    components: {
        NavigationBar,
        TabBar,
        InputText,
        InputCheckbox,
        InputDropdownFilter,
        InputDropdown,
        InputButton,
        Loader,
        SettingsHeader,
        SortedTable,
        SortLink,
    },

    data() {
        return {
            account: new Account(),
            roles: [],
            customers: [],
            locations: [],
            users: [],
            dropdownCustomers: [],
            dropdownLocations: [],
            screenLoader: new ScreenLoader(),
            showLocations: false,
            enteredFirstName: "",
            enteredLastName: "",
            enteredEmail: ""
        }
    },

    mounted() {
        this.getMetaData()
    },

    computed: {
      showCustomer () {
         return this.account.role == Constants.ROLE_BOOKING_AGENT || this.account?.customers?.length > 0// determine whether to show customer selection (booking agent)

      }
    },

    methods: {
        allowRecurringBookingClass() {
            return this.showCustomer ? 'form-input-right' : 'form-input-full'
        },

        toggleAllowRecurringBooking() {
            this.account.allowRecurringBooking = !this.account.allowRecurringBooking
        },

        toggleActive() {
            this.account.active = !this.account.active
        },

        doClear(reference) {
            this.account[reference] = ''
        },

        doClearUserFirstName() {
            this.enteredFirstName = ""
        },

        doClearUserLastName() {
            this.enteredLastName = ""
        },

        doClearUserEmail() {
            this.enteredEmail = ""
        },

        doSave() {
            if (this.account.firstName &&
                this.account.lastName &&
                this.account.emailAddress &&
                this.account.role) {
                    this.updateAccount()
            }else{
                for (const ref in this.$refs) {
                    if (this.$refs[ref] != undefined) {
                        this.$refs[ref].checkForError()
                    }
                }
            }
        },

        doCancel() {
            router.push(`/settings/accounts`)
        },

        getAccount: async function() {
            try {
                const account = await AccountService.getAccount(this.$route.params.id)
                this.handleAccountResponse(account)
            } catch (error) {
                this.screenLoader.stopWithError(error)
            }
        },

        getMetaData: async function() {
            this.screenLoader.start()

            try {
                const [customers, locations] = await Promise.all([
                    CustomerService.getCustomers(),
                    LocationService.getLocations()
                ])

                this.handleMetaDataResponse(customers, locations)
            } catch (error) {
                this.screenLoader.stopWithError(error)
            }
        },

        updateAccount: async function() {
            this.screenLoader.start()

            try {
                const response = await AccountService.updateAccount(this.account)
                this.handleUpdateAccountResponse(response, 'The account has been successfully updated')
            } catch (error) {
                this.screenLoader.stopWithError(error)
            }
        },

        handleMetaDataResponse(customers, locations) {
            this.customers = _.sortBy(customers, 'name')
            this.locations = locations
            this.dropdownCustomers = this.customers.map(o =>  new CustomerDropdown(o))
            this.dropdownLocations = locations ? locations.map(o => new LocationDropdown(o)) : []

            this.getAccount()
        },

        handleAccountResponse(account) {
            this.account.id = account.id
            this.account.firstName = account.firstName
            this.account.lastName = account.lastName
            this.account.emailAddress = account.emailAddress
            this.account.role = account.role
            this.account.customer = account.customer || {}
            this.account.customers = account.customers
            this.account.users = account.users.map(u => new CoupledUser(u))
            this.account.active = account.active
            this.account.registrationMessage = account.registrationMessage

            this.roles = [
                { id: 1, value: 'Booking agent' },
                { id: 2, value: 'Warehouse planner' },
                { id: 3, value: 'Warehouse management' },
            ]

            if (this.account.role) {
                if (KeycloakService.isSuperUser()) this.roles.push({id: 4, value: 'Super user'})
                const role = this.roles.filter(role => role.value == account.role)[0]
                this.updateRole(role.id)
            }

            this.account.locations = account.locations || []



            this.screenLoader.stop()
        },

        handleUpdateAccountResponse(response, message) {
            this.$store.dispatch(Constants.STORE_ACTION_CREATE_ALERT, {code: response.status, message: message })
            router.push(`/settings/accounts`)
        },

        /**
         * Function triggered by (re) assiging a role to the account
         * @param id
         */
        updateRole(id) {
            let currentRole = this.roles.find(r => r.value == this.account.role)
            let roleId = currentRole == undefined ? undefined : currentRole.id
            // if the role changes remove the associated customers and locations
            if (id != roleId) {
                this.account.customers = this.account?.customers ?? []
                this.account.locations = []
            }

            this.account.role = this.roles.filter(role => role.id == id)[0].value // set the new role
            this.showLocations = this.account.role != Constants.ROLE_BOOKING_AGENT && this.account.role != Constants.ROLE_SUPER_USER // determine whether to show location selection (Warehouse planner / manager)
            this.account.locations = []

        },

        addCustomer(id) {
            this.removeCustomer(id)
            this.account.customers.push(this.customers.filter(customer => customer.id == id)[0])
            this.account.locations = []
        },

        addCoupledUser() {
            let user = new CoupledUser()
            user.firstName = this.enteredFirstName
            user.lastName = this.enteredLastName
            user.email = this.enteredEmail
            this.account.users.push(user)
        },

        removeCoupledUser(email) {
            this.account.users = this.account.users.filter(user => user.email != email)
        },

        removeCustomer(id) {
            this.account.customers = this.account.customers.filter(customer => customer.id != id)
        },

        addLocation(id) {
            this.removeLocation(id)
            this.account.locations.push(this.locations.filter(location => location.id == id)[0])
        },

        removeLocation(id) {
            this.account.locations = this.account.locations.filter(location => location.id != id)
        }
    }
}
